import React, { createContext, useContext, useRef, useMemo } from "react";
import { GAME_LAYOUT } from "../settings";

export const ReelRefsContext = createContext(null);

// Initialize HoldAndWin tension overlay refs structure using settings constants
const INITIAL_HOLD_AND_WIN_TENSION_OVERLAYS = Array(GAME_LAYOUT.REEL_COUNT)
  .fill(null)
  .map(() => Array(GAME_LAYOUT.SYMBOLS_VISIBLE).fill(null));

export const ReelRefsProvider = ({ children }) => {
  const reelRefs = useRef([]);
  const overlayReelRefs = useRef([]);
  const infoReelRefs = useRef([]);
  const tensionReelRefs = useRef([]);
  const winLinesContainerRef = useRef(null);
  const winBoxContainerRef = useRef(null);
  const dimReelSetRef = useRef(null);
  const globalLineWinTextRef = useRef(null);
  const lineWinTextRef = useRef(null);

  // Free Spin
  const freeSpinReelRefs = useRef([]);
  const freeSpinOverlayReelRefs = useRef([]);
  const maskReelRefs = useRef([]);
  const freeSpinWinLinesContainerRef = useRef(null);
  const freeSpinSceneTransitionRef = useRef(null);
  const reelSetMaskRef = useRef(null);

  // HoldAndWin
  const holdAndWinReelRefs = useRef([]);
  const holdAndWinOverlayReelRefs = useRef([]);
  const holdAndWinTensionOverlayRefs = useRef(INITIAL_HOLD_AND_WIN_TENSION_OVERLAYS);
  const holdAndWinCollectorControllerRef = useRef(null);
  const holdAndWinCollectorDisplayRef = useRef(null);
  const holdAndWinCoinCollectRef = useRef(null);
  const holdAndWinCounterRef = useRef(null);
  const holdAndWinProgressBarRef = useRef(null);
  const reelFrameDecorationRef = useRef(null);

  // Royal Collect
  const royalCollectReelRefs = useRef([]);
  const royalCollectOverlayReelRefs = useRef([]);
  const royalCollectCollectionLayerRef = useRef(null);
  const royalCollectReelFrontControllerRef = useRef(null);
  const royalCollectRespinCounterRef = useRef(null);

  // Pot Collection
  const potCollectionReelRefs = useRef([]);
  const potCollectionOverlayReelRefs = useRef([]);
  const potCollectionCollectionLayerRef = useRef(null);
  const potCollectionRespinCounterRef = useRef(null);
  const potCollectionCoinCounterRef = useRef(null);
  const pcPlayCounterRef = useRef(null); // play button counter text, controlled by respin counter

  // Button refs for animation triggers
  const playButtonRef = useRef(null);

  // Game container ref for tension shake animation
  const gameContainerRef = useRef(null);

  // Background animation controller ref for background spine animations
  const backgroundAnimationControllerRef = useRef(null);

  // Character animation controller ref
  const characterAnimationControllerRef = useRef(null);

  // Background decoration controller ref for front_mask overlay (tree/flowers)
  const backgroundDecorationControllerRef = useRef(null);

  // Jackpots controller refs — separate for base game and RC to avoid ref collision
  const jackpotsControllerRef = useRef(null);     // base game
  const rcJackpotsControllerRef = useRef(null);   // Royal Collect bonus

  // POT Charging — animator ref for Zeus symbol → POT particle trails
  const potChargingAnimatorRef = useRef(null);

  // POT Controllers — { potType: PotController } for trail arrival feedback
  const potControllersRef = useRef(null);

  // GameView controller ref — for triggering scene transitions (GVC.playTransition)
  const gameViewControllerRef = useRef(null);

  // Synchronous visual cleanup callback — populated by useWinController,
  // called by ReelControlContext.handleSpin to clear all win artifacts in ONE frame.
  const snapClearAllRef = useRef(null);



  const refs = useMemo(
    () => ({
      reelRefs,
      overlayReelRefs,
      infoReelRefs,
      tensionReelRefs,
      winLinesContainerRef,
      winBoxContainerRef,
      dimReelSetRef,
      globalLineWinTextRef,
      lineWinTextRef,
      // Free Spin
      freeSpinReelRefs,
      freeSpinOverlayReelRefs,
      maskReelRefs,
      freeSpinWinLinesContainerRef,
      freeSpinSceneTransitionRef,
      reelSetMaskRef,
      // HoldAndWin
      holdAndWinReelRefs,
      holdAndWinOverlayReelRefs,
      holdAndWinTensionOverlayRefs,
      holdAndWinCollectorControllerRef,
      holdAndWinCollectorDisplayRef,
      holdAndWinCoinCollectRef,
      holdAndWinCounterRef,
      holdAndWinProgressBarRef,
      reelFrameDecorationRef,
      // Royal Collect
      royalCollectReelRefs,
      royalCollectOverlayReelRefs,
      royalCollectCollectionLayerRef,
      royalCollectReelFrontControllerRef,
      royalCollectRespinCounterRef,
      // Pot Collection
      potCollectionReelRefs,
      potCollectionOverlayReelRefs,
      potCollectionCollectionLayerRef,
      potCollectionRespinCounterRef,
      potCollectionCoinCounterRef,
      pcPlayCounterRef,
      // Button refs for animation triggers
      playButtonRef,
      // Game container ref for tension shake animation
      gameContainerRef,
      // Background animation controller ref
      backgroundAnimationControllerRef,
      // Character animation controller ref
      characterAnimationControllerRef,
      // Background decoration controller ref
      backgroundDecorationControllerRef,
      // Jackpots controller refs
      jackpotsControllerRef,
      rcJackpotsControllerRef,
      // POT Charging
      potChargingAnimatorRef,
      potControllersRef,
      // GameView controller
      gameViewControllerRef,
      // Synchronous visual cleanup
      snapClearAllRef,
    }),
    []
  );

  return (
    <ReelRefsContext.Provider value={refs}>{children}</ReelRefsContext.Provider>
  );
};

export const useReelRefs = () => {
  const ctx = useContext(ReelRefsContext);
  if (!ctx) throw new Error("useReelRefs must be used within ReelRefsProvider");
  return ctx;
};
